import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixRestaurantsOrdersettingsV3OrderSettings from './restaurants-ordersettings-v3-order-settings-order-settings.http.js';

/** OrderSettings is the main entity of OrderSettings service */
export interface OrderSettings {
  /**
   * Order Settings id
   * @format GUID
   * @immutable
   */
  _id?: string | null;
  /**
   * Restaurant location id
   * @format GUID
   */
  restaurantLocationId?: string | null;
  /** Is Order settings enabled - customers can order through the system */
  enabled?: boolean | null;
  /** if UOU need to provide a valid google maps address */
  approximateAddresses?: ApproximateAddressesWithLiterals;
  /** Local regulations may require you to add a policy document to your site. */
  policies?: Policies;
  tips?: Tips;
  futureOrders?: FutureOrdersSettings;
  /** Represents only exceptions of when the user wants to turn off the online ordering for a period of time */
  availability?: Availability;
  /** @immutable */
  revision?: string | null;
  /** Restaurant charge rounding strategy */
  chargeRoundingStrategy?: ChargeRoundingStrategyWithLiterals;
}

export enum ApproximateAddresses {
  EMPTY = 'EMPTY',
  BLOCK = 'BLOCK',
  ALLOW = 'ALLOW',
}

/** @enumType */
export type ApproximateAddressesWithLiterals =
  | ApproximateAddresses
  | 'EMPTY'
  | 'BLOCK'
  | 'ALLOW';

/** Local regulations may require you to add a policy document to your site. */
export interface Policies {
  /** Privacy policy */
  privacyPolicy?: Policy;
  /** Terms and Conditions */
  termsAndConditions?: Policy;
  /** Is the Policies feature enabled, default value false */
  enabled?: boolean;
  /** Is the checkbox of the policy agreement marked by default */
  checkedByDefault?: boolean | null;
}

export interface Policy {
  /** Default Policy type */
  defaultType?: TypeWithLiterals;
  text?: string | null;
  /** @format WEB_URL */
  url?: string | null;
}

export enum Type {
  /** Add a page to your site and put your policy document there. You can link to it from here. */
  URL = 'URL',
  /** The policy will be displayed as plain text in a popup. */
  TEXT = 'TEXT',
}

/** @enumType */
export type TypeWithLiterals = Type | 'URL' | 'TEXT';

/** Customers can leave a tip when placing an order. */
export interface Tips {
  /** Allow customers to leave a custom tips, default `false` */
  allowCustom?: boolean | null;
  /** tip type to display */
  displayType?: TipsTypeWithLiterals;
  /**
   * Set different tipping suggestion options with exact amounts, for your customers to choose from
   * @minSize 3
   * @maxSize 3
   */
  suggestedAmounts?: TippingRate[];
  /**
   * Set different tipping rate options with percentage values, for your customers to choose from
   * @minSize 3
   * @maxSize 3
   */
  suggestedPercentages?: TippingRate[];
  /** Is the Tips feature enabled, default value false */
  enabled?: boolean;
}

export enum TipsType {
  /** Tip with exact amount ($) */
  AMOUNT = 'AMOUNT',
  /** Tip in percentage */
  PERCENTAGE = 'PERCENTAGE',
}

/** @enumType */
export type TipsTypeWithLiterals = TipsType | 'AMOUNT' | 'PERCENTAGE';

export interface TippingRate {
  /**
   * Can be exact amount or percentage
   * @decimalValue options { maxScale:2 }
   */
  value?: string;
  /** Is default option */
  default?: boolean;
}

export interface FutureOrdersSettings {
  /** Is Future Orders enabled */
  enabled?: boolean | null;
  /** latest time in advance in minutes that customers can place an order */
  latestOrderBy?: number | null;
  /** earliest time in advance in minutes that customers can place an order */
  earliestOrderBy?: number | null;
  /** Allow asap orders or not. If false, only future orders are allowed */
  enableAsapOrders?: boolean | null;
}

export interface Availability {
  /**
   * Weekly recurring time periods when the entity is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the entity's regular availability. The entity can be available or not available during the special hour period.
   * @maxSize 100
   */
  specialHourPeriods?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the entity is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https:en.wikipedia.orgwikiISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https:en.wikipedia.orgwikiISO_8601) extended format. Valid values are 00:00-24:00, where 24:00 represents
   * midnight at the end of the specified day.
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

export enum DayOfWeek {
  MON = 'MON',
  TUE = 'TUE',
  WED = 'WED',
  THU = 'THU',
  FRI = 'FRI',
  SAT = 'SAT',
  SUN = 'SUN',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https:en.wikipedia.orgwikiISO_8601) format and [Coordinated Universal Time (UTC)](https:en.wikipedia.orgwikiCoordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https:en.wikipedia.orgwikiISO_8601) format and [Coordinated Universal Time (UTC)](https:en.wikipedia.orgwikiCoordinated_Universal_Time). */
  endDate?: string;
  /** Whether the item is available during the exception. Defaults to `false`. */
  available?: boolean;
  /** Name of the special hour period. In the Business Manager the special hour period is called event. */
  eventName?: string | null;
}

export enum ChargeRoundingStrategy {
  UNDEFINED = 'UNDEFINED',
  /** https://en.wikipedia.org/wiki/Rounding#Round_half_away_from_zero */
  HALF_AWAY_FROM_ZERO = 'HALF_AWAY_FROM_ZERO',
  /** https://en.wikipedia.org/wiki/Rounding#Round_half_to_even */
  HALF_EVEN = 'HALF_EVEN',
}

/** @enumType */
export type ChargeRoundingStrategyWithLiterals =
  | ChargeRoundingStrategy
  | 'UNDEFINED'
  | 'HALF_AWAY_FROM_ZERO'
  | 'HALF_EVEN';

export interface GetOrderSettingsRequest {
  /**
   * Id of the OrderSettings to retrieve
   * @format GUID
   */
  orderSettingsId: string;
}

export interface GetOrderSettingsResponse {
  /** The retrieved OrderSettings */
  orderSettings?: OrderSettings;
}

export interface UpdateOrderSettingsRequest {
  /** OrderSettings to be updated, may be partial */
  orderSettings: OrderSettings;
}

export interface UpdateOrderSettingsResponse {
  /** The updated OrderSettings */
  orderSettings?: OrderSettings;
}

export interface ListOrderSettingsRequest {
  /**
   * Ids of the restaurant locations for the OrderSettings to retrieve
   * @format GUID
   */
  restaurantLocationIds?: string[];
}

export interface ListOrderSettingsResponse {
  /** The retrieved order settings */
  orderSettings?: OrderSettings[];
}

/**
 * Get a OrderSettings by id
 * @param orderSettingsId - Id of the OrderSettings to retrieve
 * @internal
 * @documentationMaturity preview
 * @requiredField orderSettingsId
 * @permissionId WIX_RESTAURANTS.READ_ORDER_SETTINGS
 * @applicableIdentity APP
 * @returns The retrieved OrderSettings
 * @fqn com.wixpress.restaurants.ordersettings.v3.OrderSettingsApi.GetOrderSettings
 */
export async function getOrderSettings(
  orderSettingsId: string
): Promise<
  NonNullablePaths<
    OrderSettings,
    | `approximateAddresses`
    | `policies.privacyPolicy.defaultType`
    | `policies.enabled`
    | `tips.displayType`
    | `tips.suggestedAmounts`
    | `tips.suggestedAmounts.${number}.value`
    | `tips.suggestedAmounts.${number}.default`
    | `tips.suggestedPercentages`
    | `tips.enabled`
    | `availability.periods`
    | `availability.periods.${number}.openDay`
    | `availability.periods.${number}.openTime`
    | `availability.periods.${number}.closeDay`
    | `availability.periods.${number}.closeTime`
    | `availability.specialHourPeriods`
    | `availability.specialHourPeriods.${number}.startDate`
    | `availability.specialHourPeriods.${number}.endDate`
    | `availability.specialHourPeriods.${number}.available`
    | `chargeRoundingStrategy`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    orderSettingsId: orderSettingsId,
  });

  const reqOpts =
    ambassadorWixRestaurantsOrdersettingsV3OrderSettings.getOrderSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.orderSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { orderSettingsId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['orderSettingsId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Update a OrderSettings, supports partial update
 * @param _id - Order Settings id
 * @internal
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField orderSettings
 * @requiredField orderSettings.revision
 * @permissionId WIX_RESTAURANTS.MANAGE_ORDER_SETTINGS
 * @applicableIdentity APP
 * @returns The updated OrderSettings
 * @fqn com.wixpress.restaurants.ordersettings.v3.OrderSettingsApi.UpdateOrderSettings
 */
export async function updateOrderSettings(
  _id: string,
  orderSettings: NonNullablePaths<UpdateOrderSettings, `revision`, 2>
): Promise<
  NonNullablePaths<
    OrderSettings,
    | `approximateAddresses`
    | `policies.privacyPolicy.defaultType`
    | `policies.enabled`
    | `tips.displayType`
    | `tips.suggestedAmounts`
    | `tips.suggestedAmounts.${number}.value`
    | `tips.suggestedAmounts.${number}.default`
    | `tips.suggestedPercentages`
    | `tips.enabled`
    | `availability.periods`
    | `availability.periods.${number}.openDay`
    | `availability.periods.${number}.openTime`
    | `availability.periods.${number}.closeDay`
    | `availability.periods.${number}.closeTime`
    | `availability.specialHourPeriods`
    | `availability.specialHourPeriods.${number}.startDate`
    | `availability.specialHourPeriods.${number}.endDate`
    | `availability.specialHourPeriods.${number}.available`
    | `chargeRoundingStrategy`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    orderSettings: { ...orderSettings, id: _id },
  });

  const reqOpts =
    ambassadorWixRestaurantsOrdersettingsV3OrderSettings.updateOrderSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.orderSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { orderSettings: '$[1]' },
        explicitPathsToArguments: { 'orderSettings.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'orderSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateOrderSettings {
  /**
   * Order Settings id
   * @format GUID
   * @immutable
   */
  _id?: string | null;
  /**
   * Restaurant location id
   * @format GUID
   */
  restaurantLocationId?: string | null;
  /** Is Order settings enabled - customers can order through the system */
  enabled?: boolean | null;
  /** if UOU need to provide a valid google maps address */
  approximateAddresses?: ApproximateAddressesWithLiterals;
  /** Local regulations may require you to add a policy document to your site. */
  policies?: Policies;
  tips?: Tips;
  futureOrders?: FutureOrdersSettings;
  /** Represents only exceptions of when the user wants to turn off the online ordering for a period of time */
  availability?: Availability;
  /** @immutable */
  revision?: string | null;
  /** Restaurant charge rounding strategy */
  chargeRoundingStrategy?: ChargeRoundingStrategyWithLiterals;
}

/**
 * List Order settings
 * @internal
 * @documentationMaturity preview
 * @permissionId WIX_RESTAURANTS.READ_ORDER_SETTINGS
 * @applicableIdentity APP
 * @fqn com.wixpress.restaurants.ordersettings.v3.OrderSettingsApi.ListOrderSettings
 */
export async function listOrderSettings(
  options?: ListOrderSettingsOptions
): Promise<
  NonNullablePaths<
    ListOrderSettingsResponse,
    | `orderSettings`
    | `orderSettings.${number}.approximateAddresses`
    | `orderSettings.${number}.policies.privacyPolicy.defaultType`
    | `orderSettings.${number}.policies.enabled`
    | `orderSettings.${number}.tips.displayType`
    | `orderSettings.${number}.tips.enabled`
    | `orderSettings.${number}.chargeRoundingStrategy`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    restaurantLocationIds: options?.restaurantLocationIds,
  });

  const reqOpts =
    ambassadorWixRestaurantsOrdersettingsV3OrderSettings.listOrderSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          restaurantLocationIds: '$[0].restaurantLocationIds',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListOrderSettingsOptions {
  /**
   * Ids of the restaurant locations for the OrderSettings to retrieve
   * @format GUID
   */
  restaurantLocationIds?: string[];
}
